import React, { useState, useEffect } from "react";
import {
    Search,
    Calendar as CalendarIcon,
    Filter,
    Users,
    CheckCircle2,
    XCircle,
    AlertCircle,
    Clock,
    ChevronDown,
} from "lucide-react";
import { apiGet } from "../../utils/api";
import { displayName } from "../../utils/displayName";
import "./analytics.css";
import "./allAttendance.css";

const StatusBadge = ({ status }) => {
    switch (status) {
        case "Present":  return <span className="status-badge status-present"><CheckCircle2 size={11} /> Present</span>;
        case "Late":     return <span className="status-badge status-late"><AlertCircle size={11} /> Late</span>;
        case "Leave":    return <span className="status-badge status-late"><AlertCircle size={11} /> On Leave</span>;
        case "Holiday":  return <span className="status-badge">Holiday</span>;
        default:         return <span className="status-badge status-absent"><XCircle size={11} /> Absent</span>;
    }
};

const ManagerDashboard = () => {
    const [raw, setRaw]                   = useState(null);
    const [loading, setLoading]           = useState(true);
    const [searchTerm, setSearchTerm]     = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [dateFilter, setDateFilter]     = useState(new Date().toISOString().split("T")[0]);
    const [selectedTeamId, setSelectedTeamId] = useState("all");

    useEffect(() => { fetchData(); }, [dateFilter]);

    const fetchData = async () => {
        if (!raw) setLoading(true);
        try {
            const res    = await apiGet(`/api/analytics/manager-dashboard?filter=daily&date=${dateFilter}`);
            const result = await res.json().catch(() => null);
            if (res.ok && result?.success) setRaw(result);
        } catch (err) {
            console.error("Manager dashboard fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    const departments = raw?.departments || [];
    const summary     = raw?.summary    || {};
    const employees   = raw?.employees  || [];

    const allRows = employees.map((emp) => {
        const att = (emp.attendance || []).find((a) => a.date === dateFilter);
        let status = "Absent";
        if (att?.isLeave)        status = "Leave";
        else if (att?.isHoliday) status = "Holiday";
        else if (att)            status = att.lateMinutes > 0 ? "Late" : "Present";
        return {
            id:       emp.agentId,
            name:     displayName(emp),
            deptId:   String(emp.departmentId || ""),
            deptName: emp.departmentName || "Unassigned",
            checkIn:  att?.checkInTime  ? new Date(att.checkInTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—",
            checkOut: att?.checkOutTime ? new Date(att.checkOutTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—",
            status,
            hours: ((att?.onlineMs || 0) / 3_600_000).toFixed(1) + "h",
        };
    });

    // Filter by selected team
    const teamRows = selectedTeamId === "all"
        ? allRows
        : allRows.filter((r) => {
            const dept = departments.find((d) => String(d._id) === selectedTeamId);
            return r.deptId === selectedTeamId || (dept && r.deptName === dept.name);
        });

    // Apply search + status filters
    const filteredRows = teamRows.filter((row) => {
        const q = searchTerm.toLowerCase();
        const matchSearch = row.name.toLowerCase().includes(q) || row.deptName.toLowerCase().includes(q);
        const matchStatus = statusFilter === "all" || row.status.toLowerCase() === statusFilter.toLowerCase();
        return matchSearch && matchStatus;
    });

    // Summary counts for currently selected scope
    const scopePresent = teamRows.filter((r) => r.status === "Present" || r.status === "Late").length;
    const scopeLeave   = teamRows.filter((r) => r.status === "Leave").length;
    const scopeAbsent  = teamRows.filter((r) => r.status === "Absent").length;

    const selectedDept = departments.find((d) => String(d._id) === selectedTeamId);

    if (!loading && departments.length === 0) {
        return (
            <div className="attendance-view">
                <header className="attendance-header">
                    <div className="header-left">
                        <h1>Manager Dashboard</h1>
                        <p>Your team's attendance and activity overview</p>
                    </div>
                </header>
                <div className="attendance-table-container">
                    <div className="empty-state">
                        No team has been assigned to you yet. Contact your admin to assign departments.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="attendance-view">

            {/* Page header */}
            <header className="attendance-header">
                <div className="header-left">
                    <h1>Manager Dashboard</h1>
                    <p>
                        {selectedTeamId === "all"
                            ? `All teams (${departments.length})`
                            : selectedDept?.name || "Team"
                        } &middot; {dateFilter}
                    </p>
                </div>
            </header>

            {/* Summary cards */}
            <div className="summary-cards">
                <div className="summary-card present">
                    <Users size={24} />
                    <div className="card-info">
                        <span className="card-value">{teamRows.length || summary.totalEmployees || 0}</span>
                        <span className="card-label">Total Members</span>
                    </div>
                </div>
                <div className="summary-card leave">
                    <CheckCircle2 size={24} />
                    <div className="card-info">
                        <span className="card-value">{scopePresent}</span>
                        <span className="card-label">Present Today</span>
                    </div>
                </div>
                <div className="summary-card holiday">
                    <AlertCircle size={24} />
                    <div className="card-info">
                        <span className="card-value">{scopeLeave}</span>
                        <span className="card-label">On Leave</span>
                    </div>
                </div>
                <div className="summary-card absent">
                    <XCircle size={24} />
                    <div className="card-info">
                        <span className="card-value">{scopeAbsent}</span>
                        <span className="card-label">Absent</span>
                    </div>
                </div>
            </div>

            {/* Filters row */}
            <div className="attendance-filters">
                {/* Team dropdown */}
                <div className="mgr-team-select-wrap select-wrapper">
                    <Users size={16} className="filter-icon" />
                    <select
                        value={selectedTeamId}
                        onChange={(e) => {
                            setSelectedTeamId(e.target.value);
                            setSearchTerm("");
                            setStatusFilter("all");
                        }}
                    >
                        <option value="all">All Teams</option>
                        {departments.map((dept) => (
                            <option key={dept._id} value={String(dept._id)}>{dept.name}</option>
                        ))}
                    </select>
                </div>

                <div className="search-box">
                    <Search size={16} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search employees…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="filter-group">
                    <div className="date-picker-wrapper">
                        <CalendarIcon size={16} className="filter-icon" />
                        <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
                    </div>
                    <div className="select-wrapper">
                        <Filter size={16} className="filter-icon" />
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="all">All Status</option>
                            <option value="present">Present</option>
                            <option value="late">Late</option>
                            <option value="leave">Leave</option>
                            <option value="absent">Absent</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Employee table */}
            <div className="attendance-table-container">
                <table className="attendance-table">
                    <thead>
                        <tr>
                            <th>Employee Name</th>
                            {selectedTeamId === "all" && <th>Team</th>}
                            <th>Check-in</th>
                            <th>Check-out</th>
                            <th>Hours</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading
                            ? Array.from({ length: 6 }).map((_, i) => (
                                <tr key={i} className="skeleton-row">
                                    <td colSpan={selectedTeamId === "all" ? 6 : 5}>
                                        <div className="skeleton-line" />
                                    </td>
                                </tr>
                            ))
                            : filteredRows.length > 0
                                ? filteredRows.map((row) => (
                                    <tr key={row.id}>
                                        <td>{row.name}</td>
                                        {selectedTeamId === "all" && <td style={{ color: "var(--text-muted)", fontSize: "12px" }}>{row.deptName}</td>}
                                        <td>{row.checkIn}</td>
                                        <td>{row.checkOut}</td>
                                        <td className="hours-cell">{row.hours}</td>
                                        <td><StatusBadge status={row.status} /></td>
                                    </tr>
                                ))
                                : (
                                    <tr>
                                        <td colSpan={selectedTeamId === "all" ? 6 : 5} className="empty-state">
                                            No records found for the selected filters.
                                        </td>
                                    </tr>
                                )
                        }
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ManagerDashboard;
